import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Paper, Backdrop } from '@mui/material';
import { Lock, WarningAmber, Block } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const SubscriptionRestrictionContext = createContext();

export const useSubscriptionRestriction = () => useContext(SubscriptionRestrictionContext);

// Priority: Higher number means higher priority
const PRIORITY_MAP = {
  'SUBSCRIPTION_EXPIRED': 3,
  'FEATURE_NOT_AVAILABLE': 2,
  'PLAN_UPGRADE_REQUIRED': 2,
  'LIMIT_REACHED': 1,
  'SOFT_LIMIT_WARNING': 0
};

export const SubscriptionRestrictionProvider = ({ children }) => {
  const [modalState, setModalState] = useState({ open: false, data: null });
  const [overlayState, setOverlayState] = useState({ active: false, data: null });
  const [softLimit, setSoftLimit] = useState({ active: false, data: null });

  const currentPriorityRef = useRef(-1);
  const debounceTimerRef = useRef(null);
  const broadcastChannelRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Persistent memory to prevent identical restriction loops
  const lastShownRestrictionRef = useRef({ code: null, feature: null, timestamp: 0 });

  const { user } = useAuth();

  // Normalize path for whitelist check
  const currentPath = useMemo(() => {
    const path = location.pathname.toLowerCase().replace(/\/$/, '') || '/';
    return path;
  }, [location.pathname]);

  // Determine if current path is part of the public website or subscription/pricing flow
  const isExcludedPath = useMemo(() => {
    const websitePaths = [
      '/', '/home', '/features', '/pricing', '/about', '/faqs', '/contact',
      '/demo', '/privacy', '/terms', '/cookie-policy', '/dpa', '/security',
      '/api-docs', '/partner', '/verify', '/403', '/login', '/register',
      '/forgot-password', '/subscription', '/subscription-settings', '/pricing-plans', '/stepwise-register'
    ];

    // Multi-source path check for maximum resilience
    const pathFromHook = currentPath;
    const pathFromWindow = window.location.pathname.toLowerCase().replace(/\/$/, '') || '/';

    const isSubscriptionPath = pathFromHook.includes('subscription') || pathFromWindow.includes('subscription');
    const isPricingPath = pathFromHook.includes('pricing') || pathFromWindow.includes('pricing');

    const excluded = websitePaths.includes(pathFromHook) ||
      websitePaths.includes(pathFromWindow) ||
      isSubscriptionPath ||
      isPricingPath ||
      pathFromHook.startsWith('/verify/') ||
      pathFromWindow.startsWith('/verify/');

    console.debug('[SubscriptionRestriction] Path Audit:', {
      pathFromHook,
      pathFromWindow,
      excluded,
      active: overlayState.active
    });

    return excluded;
  }, [currentPath, overlayState.active]);

  // Reset all restrictions if user is Super Admin
  useEffect(() => {
    const userRole = (user?.userRole?.name || user?.role || '').toLowerCase().replace(' ', '_');
    if (userRole === 'super_admin') {
      setModalState({ open: false, data: null });
      setOverlayState({ active: false, data: null });
      setSoftLimit({ active: false, data: null });
      currentPriorityRef.current = -1;
    }
  }, [user]);

  useEffect(() => {
    // Cross-tab synchronization
    broadcastChannelRef.current = new BroadcastChannel('fluxerp_subscription_channel');

    const handleRestriction = (event) => {
      const data = event.detail || event.data;
      const isFromBroadcast = !!event.data;

      // 0. Super Admin Bypass: Ensure Super Admins never see a restriction overlay or modal
      const userRole = (user?.userRole?.name || user?.role || '').toLowerCase().replace(' ', '_');
      if (userRole === 'super_admin') {
        return;
      }

      // Broadcast to other tabs if this was triggered locally
      if (!isFromBroadcast && broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage(data);
      }

      const incomingPriority = PRIORITY_MAP[data.code] || 0;

      // 1. Structured Logging for Enterprise Observability
      if (!isFromBroadcast) {
        console.warn(JSON.stringify({
          event: 'SUBSCRIPTION_RESTRICTION_TRIGGERED',
          timestamp: new Date().toISOString(),
          userId: user?.id || 'anonymous',
          companyId: user?.companyId || 'none',
          requestId: data.requestId || 'unknown',
          source: 'UI',
          restrictionData: data
        }));
      }

      // 2. Persistent Memory Spam Protection
      const isSameRestriction =
        lastShownRestrictionRef.current.code === data.code &&
        lastShownRestrictionRef.current.feature === data.feature;

      const timeSinceLast = Date.now() - lastShownRestrictionRef.current.timestamp;

      if (isSameRestriction && timeSinceLast < 2000) {
        return; // Ignore repetitive spam
      }

      // Priority system: Do not override a higher-priority modal/overlay
      if (incomingPriority < currentPriorityRef.current) {
        return;
      }

      // Debounce logic to prevent rapid-fire stacking
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        currentPriorityRef.current = incomingPriority;
        lastShownRestrictionRef.current = { code: data.code, feature: data.feature, timestamp: Date.now() };

        if (data.code === 'SUBSCRIPTION_EXPIRED' || data.code === 'SUBSCRIPTION_SUSPENDED') {
          // Expiration or suspension overrides everything
          setModalState({ open: false, data: null });
          setOverlayState({ active: true, data });
        } else if (['LIMIT_REACHED', 'FEATURE_NOT_AVAILABLE', 'PLAN_UPGRADE_REQUIRED'].includes(data.code)) {
          // If overlay is active, ignore modals
          if (!overlayState.active) {
            setModalState({ open: true, data });
          }
        } else if (data.code === 'SOFT_LIMIT_WARNING') {
          if (!overlayState.active && !modalState.open) {
            setSoftLimit({ active: true, data });
          }
        }
      }, 100); // 100ms debounce
    };

    window.addEventListener('subscription-restriction', handleRestriction);
    broadcastChannelRef.current.addEventListener('message', handleRestriction);

    return () => {
      window.removeEventListener('subscription-restriction', handleRestriction);
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.removeEventListener('message', handleRestriction);
        broadcastChannelRef.current.close();
      }
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [overlayState.active, modalState.open, user]);

  const closeModal = () => {
    setModalState({ open: false, data: null });
    currentPriorityRef.current = -1;
    lastShownRestrictionRef.current = { code: null, feature: null, timestamp: 0 };
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({ action: 'CLOSE_MODAL' });
    }
  };

  const closeSoftLimit = () => {
    setSoftLimit({ active: false, data: null });
    if (currentPriorityRef.current === 0) currentPriorityRef.current = -1;
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({ action: 'CLOSE_SOFT_LIMIT' });
    }
  };

  // Listen for close actions from other tabs
  useEffect(() => {
    const handleCloseSync = (event) => {
      if (event.data?.action === 'CLOSE_MODAL') {
        setModalState({ open: false, data: null });
        currentPriorityRef.current = -1;
        lastShownRestrictionRef.current = { code: null, feature: null, timestamp: 0 };
      } else if (event.data?.action === 'CLOSE_SOFT_LIMIT') {
        setSoftLimit({ active: false, data: null });
        if (currentPriorityRef.current === 0) currentPriorityRef.current = -1;
      }
    };

    const channel = new BroadcastChannel('fluxerp_subscription_channel');
    channel.addEventListener('message', handleCloseSync);
    return () => {
      channel.removeEventListener('message', handleCloseSync);
      channel.close();
    };
  }, []);

  return (
    <SubscriptionRestrictionContext.Provider value={{
      closeModal,
      closeSoftLimit
    }}>
      {children}

      {/* 1. Global Soft Limit Banner */}
      {softLimit.active && softLimit.data && (
        <Paper elevation={0} sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200,
          bgcolor: 'warning.light', color: 'warning.contrastText',
          p: 1.5, display: 'flex', justifyContent: 'center', alignItems: 'center',
          borderBottom: 1, borderColor: 'warning.main'
        }}>
          <WarningAmber sx={{ mr: 1 }} />
          <Typography variant="body2" sx={{ flexGrow: 1, maxWidth: '800px', textAlign: 'center' }}>
            {softLimit.data.message || 'You are approaching your plan limits. Upgrade soon to avoid interruptions.'}
          </Typography>
          {softLimit.data.canUpgrade ? (
            <Button size="small" variant="contained" color="warning" onClick={() => navigate('/subscription')} sx={{ mr: 2 }}>
              Upgrade Now
            </Button>
          ) : (
            <Button size="small" variant="contained" color="warning" disabled sx={{ mr: 2 }}>
              Contact Admin to Upgrade
            </Button>
          )}
          <Button size="small" color="inherit" onClick={closeSoftLimit}>Dismiss</Button>
        </Paper>
      )}

      {/* 2. Non-destructive Global Modal for Limits/Features */}
      <Dialog
        open={modalState.open}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick') closeModal();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {modalState.data && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
              {modalState.data.code === 'LIMIT_REACHED' ? <WarningAmber color="warning" /> : <Lock color="action" />}
              <Typography variant="h6">
                {modalState.data.code === 'LIMIT_REACHED' ? 'Usage Limit Reached' : 'Feature Restricted'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" paragraph sx={{ color: 'text.secondary', mt: 1 }}>
                {modalState.data.canUpgrade
                  ? (modalState.data.code === 'LIMIT_REACHED'
                    ? `Your company has reached the limit of ${modalState.data.limit || ''} ${modalState.data.feature || 'items'} for the ${modalState.data.plan || 'current'} plan. To ensure uninterrupted business operations, please upgrade your plan.`
                    : `Your current ${modalState.data.plan || 'plan'} does not include access to this feature. Upgrade to gain additional capabilities.`)
                  : (modalState.data.code === 'LIMIT_REACHED'
                    ? `The limit for ${modalState.data.feature || 'this resource'} has been reached. Please contact your system administrator to upgrade the account.`
                    : `This feature requires a higher plan tier. Please contact your system administrator to request access.`)
                }
              </Typography>

              {(modalState.data.limit !== undefined && modalState.data.current !== undefined) && (
                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mt: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="subtitle2" color="text.secondary">Current Usage Details</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>{modalState.data.feature || 'Resource'}:</strong> {modalState.data.current} / {modalState.data.limit} (100% Used)
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
              <Button onClick={closeModal} color="inherit">
                {modalState.data.canUpgrade ? 'Maybe Later' : 'Close'}
              </Button>
              {modalState.data.canUpgrade && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    closeModal();
                    navigate('/subscription');
                  }}
                >
                  View Upgrade Options
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 3. Subscription Lock Overlay (For 402 Expired) */}
      <Backdrop
        sx={{
          zIndex: 9999,
          backdropFilter: 'blur(5px)',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          display: 'flex',
          flexDirection: 'column'
        }}
        open={overlayState.active && !isExcludedPath}
      >
        {overlayState.data && (
          <Paper elevation={4} sx={{ p: 4, maxWidth: 450, textAlign: 'center', borderRadius: 2, position: 'relative' }}>
            {/* Fallback: If we are somehow stuck but on an excluded path, allow manual close */}
            {isExcludedPath && (
              <Button
                size="small"
                sx={{ position: 'absolute', top: 8, right: 8 }}
                onClick={() => setOverlayState({ active: false, data: null })}
              >
                Close
              </Button>
            )}

            <Block color="error" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              {overlayState.data?.code === 'SUBSCRIPTION_SUSPENDED' ? 'Action Required: Subscription Suspended' : 'Action Required: Subscription Expired'}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {overlayState.data.canUpgrade
                ? 'Your FluxERP trial or subscription has ended. To resume normal business operations and regain access to your data, please select a plan.'
                : 'The company subscription has expired. Please contact your system administrator to renew the plan.'
              }
            </Typography>
            <Box mt={3}>
              {overlayState.data.canUpgrade ? (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={() => {
                    // Navigate to subscription page
                    navigate('/stepwise-register?upgrade=true');
                  }}
                  sx={{ py: 1.5, fontWeight: 600 }}
                >
                  Select a Plan
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => window.location.reload()}
                  sx={{ py: 1.5 }}
                >
                  Refresh Page
                </Button>
              )}
            </Box>
          </Paper>
        )}
      </Backdrop>
    </SubscriptionRestrictionContext.Provider>
  );
};
